import { useEffect, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../api/client";
import { useResults } from "../api/hooks";
import { enqueuedResponseSchema, type LocationResultsRow } from "../../api/contracts";
import type { LocationResults, MetricKey } from "../../results/types";
import { BarChart } from "../components/charts";
import { Banner, Button, EmptyState, ErrorNote, PageHeader, Panel, Select, Spinner, Stat, cx } from "../components/ui";
import { PayPanel } from "../components/PayPanel";
import { StatusDot } from "../components/StatusDot";
import { Timestamp } from "../components/Timestamp";
import { formatDay } from "../lib/format";
import { trackAction } from "../lib/usage-track";
import {
  BLOCKED_RANGES, deltaTone, firstSentence, formatHours, formatMetric, landedCaption, landedShares, metricLabel, pctChange, perWeek, signedPct, verdictBanner,
  type BlockedRange,
} from "../lib/results";

const METRICS: { key: MetricKey; label: string }[] = [
  { key: "bookings", label: "Bookings / week" }, { key: "serviceSales", label: "Service sales / week" }, { key: "newClients", label: "New clients / week" },
];
const ALL = "all";
// Figma: a rise in green, a fall in the results crimson; flat stays plain.
const TEXT_TONE = { success: "text-hs-success", warning: "text-hs-danger", neutral: "" } as const;
const BANNER_TONE = { success: "success", warning: "warning", neutral: "info" } as const;
const PART_LABEL: Record<string, string> = { first: "First hour", middle: "Middle", last: "Last hour" };
/** Chart colours: before automatic BTB in the Figma taupe, the last four weeks in crimson. */
const COMPARE_COLORS = ["#b8afa7", "#c8144f"];

/** One metric's per-week rate since go-live against a comparison window's, as a percent change. */
function vsWindow(total: number, days: number, other: number | null, otherDays: number): number | null {
  const a = perWeek(total, days);
  return a === null || other === null ? null : pctChange(a, perWeek(other, otherDays));
}

export function ResultsPage({ variant }: { variant: "owner" | "admin" }) {
  const q = useResults();
  const [selected, setSelected] = useState("");
  const refresh = useMutation({ mutationFn: () => api("/api/admin/results/refresh", enqueuedResponseSchema, { method: "POST" }) });
  const rows = q.data?.locations ?? [];
  const many = rows.length > 1;
  const showAll = many && selected === ALL;
  const row = showAll ? null : rows.find((r) => r.blvdId === selected) ?? rows[0];
  useEffect(() => { trackAction("results_viewed"); }, []); // once per mount
  return (
    <>
      <PageHeader
        title="Results"
        subtitle="How bookings, sales and new clients are doing since BTB went live."
        actions={variant === "admin" && (
          <>
            {many && (
              <Select aria-label="Location" className="w-56" value={showAll ? ALL : row?.blvdId ?? ""} onChange={(e) => setSelected(e.target.value)}>
                <option value={ALL}>All locations</option>
                {rows.map((r) => <option key={r.blvdId} value={r.blvdId}>{r.name}</option>)}
              </Select>
            )}
            <Button variant="secondary" disabled={refresh.isPending} onClick={() => refresh.mutate()}>Refresh now</Button>
          </>
        )}
      />
      <div className="space-y-7">
        {variant === "admin" && refresh.isSuccess && <Banner tone="info">Refresh queued. The numbers update in a minute or two; reload to see them.</Banner>}
        {refresh.isError && <ErrorNote error={refresh.error} />}
        {q.isPending && <Spinner />}
        {q.isError && <ErrorNote error={q.error} />}
        {q.data && rows.length === 0 && <EmptyState title="Results show up once BTB has placed its first block here." />}
        {rows.length > 0 && (
          <>
            {variant === "owner" && many && (
              <div role="group" aria-label="Location" className="flex flex-wrap gap-2">
                {rows.map((r) => {
                  const on = !showAll && r.blvdId === row?.blvdId;
                  return <Button key={r.blvdId} size="sm" variant={on ? "primary" : "secondary"} aria-pressed={on} onClick={() => setSelected(r.blvdId)}>{r.name}</Button>;
                })}
                <Button size="sm" variant={showAll ? "primary" : "secondary"} aria-pressed={showAll} onClick={() => setSelected(ALL)}>All my locations</Button>
              </div>
            )}
            {showAll ? <AllLocations rows={rows} onPick={setSelected} /> : row && <LocationView key={row.blvdId} row={row} admin={variant === "admin"} />}
          </>
        )}
      </div>
    </>
  );
}

function Delta({ pct, children }: { pct: number | null; children: ReactNode }) {
  return <span className="block text-sm font-medium text-hs-text/70"><span className={cx("mr-1 font-semibold", TEXT_TONE[deltaTone(pct)])}>{pct === null ? "—" : signedPct(pct)}</span>{children}</span>;
}

/** The Figma metric card: label, a 32px figure, a divider, then the two comparisons. */
function MetricTile({ k, label, r }: { k: MetricKey; label: string; r: LocationResults }) {
  const m = r.metrics[k], w = r.windows;
  const since = perWeek(m.since, w.since.days);
  return (
    <div className="rounded-xl border border-hs-border bg-hs-surface px-5 py-4">
      <p className="text-sm font-medium text-hs-text-muted">{label}</p>
      <p className="mt-1 text-[32px] font-semibold leading-tight text-hs-text tabular-nums">{since === null ? "—" : formatMetric(k, since)}</p>
      <div className="mt-4 space-y-4 border-t border-hs-border pt-4">
        <Delta pct={vsWindow(m.since, w.since.days, m.sinceLastYear, w.sinceLastYear.days)}>vs last year</Delta>
        <Delta pct={vsWindow(m.since, w.since.days, m.before, w.before.days)}>vs before automatic BTB</Delta>
      </div>
    </div>
  );
}

function LocationView({ row, admin }: { row: LocationResultsRow; admin: boolean }) {
  const r = row.results;
  return (
    <>
      <p className="-mt-3 text-sm font-medium text-hs-text-muted tabular-nums">Live since {formatDay(row.goLive)}{r && ` · ${r.daysLive} ${r.daysLive === 1 ? "day" : "days"}`}</p>
      {r === null ? <EmptyState title="Numbers arrive after tonight's refresh." /> : <Comparison r={r} goLive={row.goLive} />}
      {r && <PayPanel blvdId={row.blvdId} goLive={row.goLive} verdict={r.verdict} pay={r.pay} settings={row.pay} admin={admin} />}
      <Blocked blocked={row.blocked} />
      <p className="text-xs font-medium text-hs-text-muted">
        {r && <>Updated <Timestamp at={r.computedAt} zone={row.timezone} /> {"·"} </>}
        Sales and bookings come from Boulevard via the Hello Sugar data warehouse.
      </p>
    </>
  );
}

/** "+4 pts" / "−5 pts": how a part's share moved, under its x label. */
const ptsDelta = (before: number, since: number): string => {
  const d = since - before;
  return `${d < 0 ? "−" : "+"}${Math.abs(d)} ${Math.abs(d) === 1 ? "pt" : "pts"}`;
};

function Comparison({ r, goLive }: { r: LocationResults; goLive: string }) {
  const banner = verdictBanner(r.verdict, goLive);
  const before = r.landed ? landedShares(r.landed.before) : null;
  const since = r.landed ? landedShares(r.landed.since) : null;
  // Shade the part of the shift whose share moved most: the one the caption talks about.
  const moved = before && since ? [0, 1, 2].reduce((a, b) => (Math.abs(since[b] - before[b]) > Math.abs(since[a] - before[a]) ? b : a), 0) : null;
  return (
    <>
      <Banner tone={BANNER_TONE[banner.tone]}>{banner.text}</Banner>
      <div className="grid gap-6 sm:grid-cols-3">{METRICS.map((m) => <MetricTile key={m.key} k={m.key} label={m.label} r={r} />)}</div>
      {r.landed && before && since && (
        <Panel title="Did clients move or leave?" subtitle="Share of appointments by part of the shift, before automatic BTB and in the last 4 weeks.">
          <BarChart title="Share of appointments by part of the shift" x={["first", "middle", "last"]} xTick={(x) => PART_LABEL[x]} format={(v) => `${v}%`}
            colors={COMPARE_COLORS} valueLabels barWidth={32} highlight={moved} xSub={(_, i) => ptsDelta(before[i], since[i])}
            series={[{ label: "Before automatic BTB", values: before }, { label: "Last 4 weeks", values: since }]} />
          <p className="mt-3 text-sm font-medium text-hs-text">{landedCaption(r.landed.before, r.landed.since)}</p>
        </Panel>
      )}
    </>
  );
}

function Blocked({ blocked }: { blocked: LocationResultsRow["blocked"] }) {
  const [range, setRange] = useState<BlockedRange>("d30");
  const cur = BLOCKED_RANGES.find((x) => x.key === range)!, t = blocked[range];
  return (
    <Panel title="Time blocked" subtitle="How much calendar time BTB blocks held, and how much was opened back up." actions={
      <div role="group" aria-label="Range" className="flex flex-wrap gap-1">
        {BLOCKED_RANGES.map((x) => <Button key={x.key} size="sm" variant={x.key === range ? "primary" : "ghost"} aria-pressed={x.key === range} onClick={() => setRange(x.key)}>{x.label}</Button>)}
      </div>
    }>
      <div className="grid gap-6 sm:grid-cols-2">
        <Stat size="lg" tone="danger" label="Actually blocked" value={formatHours(t.blockedMinutes)} detail={`${cur.caption} · only days already over`} />
        <Stat size="lg" tone="success" label="Opened back up" value={formatHours(t.openedMinutes)} detail="planned, then freed for a booking or change" />
      </div>
    </Panel>
  );
}

function AllLocations({ rows, onPick }: { rows: LocationResultsRow[]; onPick: (id: string) => void }) {
  const lastYear = (r: LocationResultsRow, k: MetricKey) => {
    const res = r.results;
    return res ? vsWindow(res.metrics[k].since, res.windows.since.days, res.metrics[k].sinceLastYear, res.windows.sinceLastYear.days) : null;
  };
  const head = "px-5 py-3 text-left text-xs font-semibold text-hs-text-muted";
  return (
    <Panel title="All my locations" padding="none">
      <div className="overflow-x-auto border-t border-hs-border">
        <table className="w-full border-collapse text-sm font-medium">
          <thead className="bg-hs-surface-alt">
            <tr>
              <th scope="col" className={head}>Location</th>
              <th scope="col" className={head}>How it is going</th>
              {METRICS.map((m) => <th key={m.key} scope="col" className={cx(head, "text-right")}>{metricLabel(m.key)} vs last year</th>)}
              <th scope="col" className={cx(head, "text-right")}>Blocked (30 d)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const b = r.results ? verdictBanner(r.results.verdict, r.goLive) : null;
              return (
                <tr key={r.blvdId} onClick={() => onPick(r.blvdId)} className="cursor-pointer border-t border-hs-border hover:bg-hs-surface-alt">
                  <td className="px-5 py-3 font-semibold">
                    <button type="button" className="text-left underline-offset-2 hover:underline" onClick={(e) => { e.stopPropagation(); onPick(r.blvdId); }}>{r.name}</button>
                  </td>
                  <td className="px-5 py-3">
                    {b ? <StatusDot wrap tone={b.tone === "neutral" ? "neutral" : b.tone} label={firstSentence(b.text)} /> : <span className="text-hs-text-muted">Numbers arrive after tonight's refresh.</span>}
                  </td>
                  {METRICS.map((m) => {
                    const p = lastYear(r, m.key);
                    return <td key={m.key} className={cx("px-5 py-3 text-right font-semibold tabular-nums", TEXT_TONE[deltaTone(p)])}>{p === null ? "—" : signedPct(p)}</td>;
                  })}
                  <td className="px-5 py-3 text-right tabular-nums">{formatHours(r.blocked.d30.blockedMinutes)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
